import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Sparkles, RefreshCw } from 'lucide-react';

interface VerifiedAddressInputProps {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  id?: string;
  className?: string;
}

export const VerifiedAddressInput: React.FC<VerifiedAddressInputProps> = ({
  value,
  onChange,
  placeholder = "Enter physical address...",
  id = "address-input",
  className = ""
}) => {
  const [suggestions, setSuggestions] = useState<Array<{ text: string; placeId?: string }>>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [isSandbox, setIsSandbox] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);

  // Put dropdownRef on the outermost wrapper container so interaction inside is not treated as click-outside
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions with debounce
  useEffect(() => {
    // Only fetch suggestions if the user has actually typed
    if (!hasTyped) return;

    // We only fetch suggestions if the value is at least 2 characters and hasSelected is false
    if (!value || value.length < 2 || hasSelected) {
      if (!hasSelected) {
        setSuggestions([]);
      }
      return;
    }

    const timer = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const res = await fetch('/api/address/autocomplete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ input: value })
        });
        
        if (res.ok) {
          const contentType = res.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await res.json().catch(() => ({}));
            setSuggestions(data.suggestions || []);
            setIsSandbox(!!data.mock);
            if (data.suggestions && data.suggestions.length > 0) {
              setShowDropdown(true);
            }
          }
        }
      } catch (err) {
        console.warn('AutoComplete proxy lookup failed:', err);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 250); // Snappy 250ms debounce

    return () => clearTimeout(timer);
  }, [value, hasSelected, hasTyped]);

  const handleSelectSuggestion = (text: string) => {
    onChange(text);
    setHasSelected(true);
    setSuggestions([]);
    setShowDropdown(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (document.activeElement === inputRef.current) {
      setHasTyped(true);
      setHasSelected(false); // Enable tracking suggestions again since typing represents a fresh layout query
      onChange(val);
      setShowDropdown(true);
    } else {
      onChange(val);
    }
  };

  return (
    <div 
      ref={containerRef} 
      className="relative w-full" 
      id={`verified-address-group-${id}`}
    >
      <div className="relative w-full">
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={value}
          onChange={handleInputChange}
          onFocus={() => {
            if (value && value.length >= 3 && suggestions.length > 0) {
              setShowDropdown(true);
            }
          }}
          placeholder={placeholder}
          className={`w-full pr-10 focus:outline-none transition-all ${className}`}
          autoComplete="off"
          required
        />
        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center space-x-1.5 text-slate-400 pointer-events-none z-10">
          {loadingSuggestions ? (
            <RefreshCw className="w-4 h-4 animate-spin text-[#0094c8]" />
          ) : (
            <MapPin className="w-4 h-4 text-slate-400" />
          )}
        </div>

        {/* Dynamic drop suggestions dropdown with heavy stack ordering to ensure they always stay visible */}
        {showDropdown && suggestions.length > 0 && (
          <div 
            className="absolute left-0 right-0 mt-1 bg-white border border-slate-200/90 rounded-xl shadow-xl max-h-60 overflow-y-auto divide-y divide-slate-50 overflow-hidden z-[9999]"
            id={`address-autocomplete-dropdown-${id}`}
            style={{ position: 'absolute', zIndex: 9999 }}
          >
            <div className="px-3.5 py-1.5 bg-slate-50 text-[9px] uppercase tracking-wider font-extrabold text-slate-400 flex items-center justify-between sticky top-0 bg-slate-50 border-b border-slate-100">
              <span>{isSandbox ? "Address Suggestions (Sandbox)" : "Google Certified Address"}</span>
              {!isSandbox && <Sparkles className="w-3.5 h-3.5 text-[#0094c8] animate-pulse" />}
            </div>
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSelectSuggestion(suggestion.text)}
                className="w-full px-4 py-2.5 hover:bg-[#0094c8]/5 flex items-start gap-2.5 text-left text-xs font-medium transition-all group cursor-pointer"
              >
                <MapPin className="w-4 h-4 text-slate-400 group-hover:text-[#0094c8] shrink-0 mt-0.5 transition-colors" />
                <span className="text-slate-700 group-hover:text-[#011d3c]">{suggestion.text}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
